import React from "react";
import clonedeep from "lodash.clonedeep";

import { reject } from "../../../../../Common/TypingUtils";

type CallbackType = () => Promise<void>;
type ChannelName = "afterOpen";

export interface IModalFormsRegistry {
    openModal(id: string): void;
    closeModal(id: string): void;
    isOpened(id: string): boolean;

    subscribe(channelName: ChannelName, modalId: string, callback: CallbackType): VoidFunction;
    publish(channelName: ChannelName, modalId: string): Promise<void>;
    setParams(params: unknown): void;
    getParams(): unknown;
}

interface Channel {
    [modalId: string]: {
        [listenerId: string]: CallbackType;
    };
}
type Channels = Partial<Record<ChannelName, Channel>>;

const ModalFormsContext = React.createContext<IModalFormsRegistry | undefined>(undefined);

export function useModalFormsRegistry(): IModalFormsRegistry {
    return React.useContext(ModalFormsContext) ?? reject("ModalFormContext is not provided");
}

interface ModalFormsProviderProps {
    children: React.ReactNode;
    form: IForm;
}
export function ModalFormsProvider(props: ModalFormsProviderProps): JSX.Element {
    const [openedForms, setOpenedForms] = React.useState<string[]>([]);
    const channelsRef = React.useRef<Channels>({});
    const modalParams = React.useRef<unknown>(null);

    const registry: IModalFormsRegistry = {
        openModal: (id: string): void => {
            setOpenedForms(openedForms.filter(x => x !== id).concat(id));
        },
        closeModal: (id: string): void => {
            setOpenedForms(openedForms.filter(x => x !== id));
        },
        isOpened: (id: string): boolean => openedForms.includes(id),

        subscribe: function (channelName: ChannelName, modalId: string, callback: CallbackType): VoidFunction {
            const listenerId = crypto.randomUUID();
            const channels = channelsRef.current;
            const newChannels = {
                ...channels,
                [channelName]: {
                    ...channels[channelName],
                    [modalId]: {
                        ...channels[channelName]?.[modalId],
                        [listenerId]: callback,
                    },
                },
            };
            channelsRef.current = newChannels;

            return () => {
                const filteredChannels = clonedeep(channelsRef.current);
                delete filteredChannels[channelName]?.[modalId]?.[listenerId];
                channelsRef.current = filteredChannels;
            };
        },
        publish: async function (channelName: ChannelName, modalId: string): Promise<void> {
            const callbacks = Object.values(channelsRef.current[channelName]?.[modalId] ?? {});
            await Promise.all(callbacks.map(cb => cb()));
        },

        setParams: function (params: unknown) {
            modalParams.current = params;
        },

        getParams: function () {
            return modalParams.current;
        },
    };

    props.form.publicForm.modalFormsRegistry = registry;

    return <ModalFormsContext.Provider value={registry}>{props.children}</ModalFormsContext.Provider>;
}
